import { Field, Form, Formik, FormikProps } from "formik";
import { ScrollToFirstError } from "utils";
import {
  Button,
  Input,
  Textarea,
  Checkbox,
  SelectComponent,
  CalendarInput,
  DropFileInput,
  LargeSwitch,
} from "shared";
import {
  getFormConfig,
  getValidationSchema,
  getInitialValues,
  getMockData,
  InvestmentFormValues,
  InvestmentFieldConfig,
} from "./forms";
import RadioGroup from "./fields/RadioGroup";
import ComputedField from "./fields/ComputedField";
import { useGetCountriesCurrency } from "hooks/countries";
import { useGetInvestmentCurrencies } from "hooks/investments";
import "./InvestmentForm.scss";

interface InvestmentFormProps {
  slug: string;
  onSubmit: (values: InvestmentFormValues) => void;
  onCancel: () => void;
  initialData?: Partial<InvestmentFormValues>;
  isLoading?: boolean;
  submitLabel?: string;
  checkIfCurrencyExists?: (currencyUuid: string) => boolean;
}

const renderField = (
  field: InvestmentFieldConfig,
  formik: FormikProps<InvestmentFormValues>,
  currencyOptions?: any[],
  isCurrencyTransaction?: boolean,
) => {
  const { name, label, type, placeholder } = field;

  // Ukryj pola "additionalInfo" i "relianceCanView" dla transakcji walutowych
  if (
    isCurrencyTransaction &&
    (name === "additionalInfo" || name === "relianceCanView")
  ) {
    return null;
  }

  if (
    field.showWhen &&
    String(formik.values[field.showWhen.field] || "") !== field.showWhen.value
  ) {
    return null;
  }

  const error =
    (formik.touched[name] || formik.submitCount > 0) && formik.errors[name]
      ? String(formik.errors[name])
      : undefined;

  // Zmień label dla pola daty w przypadku transakcji walutowych
  const resolvedLabel =
    isCurrencyTransaction && name === "purchaseDate"
      ? "Data transakcji"
      : label;

  switch (type) {
    case "text":
    case "number": {
      const resolvedSuffix = field.computeSuffix
        ? field.computeSuffix(formik.values, currencyOptions)
        : field.suffix;
      const isDisabled = field.computeDisabled
        ? field.computeDisabled(formik.values)
        : false;
      return (
        <div className="investment-form__field" key={name}>
          <Field
            as={Input}
            name={name}
            label={resolvedLabel}
            type={type}
            placeholder={placeholder}
            step={field.step}
            precision={field.precision}
            inputMode={field.inputMode}
            maxLength={field.maxLength}
            optional={field.required === false && !isDisabled}
            error={error}
            data-suffix={resolvedSuffix}
            disabled={isDisabled}
          />
        </div>
      );
    }
    case "textarea":
      return (
        <div className="investment-form__field" key={name}>
          <Field
            component={Textarea}
            name={name}
            label={resolvedLabel}
            placeholder={placeholder}
            optional={field.required === false}
            error={error}
          />
        </div>
      );
    case "select": {
      const rawOptions =
        name === "currencyUuid" ? currencyOptions ?? [] : field.options ?? [];
      const selectOptions = field.excludeLabels?.length
        ? rawOptions.filter((o: any) => !field.excludeLabels!.includes(o.label))
        : rawOptions;
      return (
        <div className="investment-form__field" key={String(name)}>
          <Field
            as={SelectComponent}
            options={selectOptions}
            name={name}
            label={resolvedLabel}
            isSearchable={true}
            optional={field.required === false}
          />
          {isCurrencyTransaction && name === "currencyUuid" && (
            <div className="input__hint" aria-live="polite">
              Miałeś już zakupioną tę walutę. Kontynuując, dodasz nową
              transakcję do swojej puli.
            </div>
          )}
        </div>
      );
    }
    case "checkbox":
      return (
        <div
          className={`investment-form__field${
            field.wrapperClassName ? ` ${field.wrapperClassName}` : ""
          }`}
          key={String(name)}
        >
          <Field
            as={Checkbox}
            name={String(name)}
            label={field.consentText || resolvedLabel}
            checked={formik.values[name]}
            onInputChange={() => {
              const newValue = !formik.values[name];
              if (field.onChangeSideEffect) {
                field.onChangeSideEffect(
                  newValue as boolean,
                  formik.setFieldValue,
                );
              }
            }}
          />
        </div>
      );
    case "radio":
      return (
        <div className="investment-form__field" key={String(name)}>
          <RadioGroup
            name={String(name)}
            label={resolvedLabel}
            options={field.options || []}
            value={String(formik.values[name] || "")}
            onChange={(val) => formik.setFieldValue(String(name), val)}
            error={error}
            hasOtherInput={field.hasOtherInput}
            otherFieldName={field.otherFieldName}
            otherValue={
              field.otherFieldName
                ? String(formik.values[field.otherFieldName] || "")
                : ""
            }
            onOtherChange={
              field.otherFieldName
                ? (val) => formik.setFieldValue(field.otherFieldName!, val)
                : undefined
            }
          />
        </div>
      );
    case "computed":
      return (
        <div className="investment-form__field" key={name}>
          <ComputedField
            label={resolvedLabel}
            value={field.computeFn ? field.computeFn(formik.values) : ""}
            inline={field.inline}
            suffix={
              field.computeSuffix
                ? field.computeSuffix(formik.values, currencyOptions)
                : field.suffix
            }
          />
        </div>
      );
    case "date": {
      const minDate = field.computeMinDate
        ? field.computeMinDate(formik.values)
        : undefined;
      const maxDate = field.computeMaxDate
        ? field.computeMaxDate(formik.values)
        : undefined;
      return (
        <div className="investment-form__field" key={String(name)}>
          <Field
            type="date"
            name={String(name)}
            label={resolvedLabel}
            optional={field.required === false}
            component={CalendarInput}
            errors={formik.errors}
            yearMonthPicker={true}
            minDate={minDate}
            maxDate={maxDate}
          />
        </div>
      );
    }
    case "file":
      return (
        <div className="investment-form__field" key={String(name)}>
          <Field
            label={resolvedLabel}
            name={String(name)}
            id={String(name)}
            as={DropFileInput}
            accept={field.accept ?? ".pdf,.doc,.docx,.jpg,.jpeg,.png"}
            optional
            multiple={field.multiple}
            maxSize={5}
            showPreview={field.showPreview ?? false}
            description={(() => {
              const formats = (
                field.accept ?? ".pdf,.doc,.docx,.jpg,.jpeg,.png"
              )
                .split(",")
                .map((s) => s.trim().replace(/^\./, "").toUpperCase())
                .join(", ");
              return field.multiple && field.multiple > 1
                ? `Maksymalnie ${field.multiple} plików, maks. 5\u00a0MB/plik (${formats})`
                : `Maks. 5\u00a0MB (${formats})`;
            })()}
          />
        </div>
      );
    case "switch":
      return (
        <div
          className="investment-form__field investment-form__field--switch"
          key={String(name)}
        >
          <LargeSwitch
            state={String(formik.values[name] || "")}
            options={field.options || []}
            onChange={(val: string) => formik.setFieldValue(String(name), val)}
          />
        </div>
      );
    default:
      return null;
  }
};

const InvestmentForm = ({
  slug,
  onSubmit,
  onCancel,
  initialData,
  isLoading = false,
  submitLabel = "Zapisz",
  checkIfCurrencyExists,
}: InvestmentFormProps) => {
  const config = getFormConfig(slug);
  const validationSchema = getValidationSchema(slug);
  const initialValues = getInitialValues(slug, initialData);
  const { data: currencyOptions } = useGetCountriesCurrency();
  const { data: investmentCurrencyOptions } = useGetInvestmentCurrencies();
  const resolvedCurrencyOptions =
    slug === "currencies" ? investmentCurrencyOptions : currencyOptions;

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        const transformed = config.transformValues
          ? config.transformValues(values)
          : values;
        onSubmit(transformed as InvestmentFormValues);
      }}
      enableReinitialize
    >
      {(formik) => {
        const isCurrencyTransaction = !!(
          slug === "currencies" &&
          checkIfCurrencyExists &&
          formik.values.currencyUuid &&
          checkIfCurrencyExists(formik.values.currencyUuid as string)
        );

        const handleFill = () => {
          const mock = getMockData(slug, resolvedCurrencyOptions ?? []);
          formik.setValues({ ...formik.values, ...mock });
        };

        return (
          <Form className="investment-form">
            <ScrollToFirstError />
            <button
              type="button"
              className="investment-form__fill-btn"
              onClick={handleFill}
            >
              uzupełnij
            </button>
            <div className="investment-form__fields">
              {config.fields.map((field) =>
                renderField(
                  field,
                  formik,
                  resolvedCurrencyOptions,
                  isCurrencyTransaction,
                ),
              )}
            </div>

            <div className="investment-form__actions">
              <Button
                type="button"
                onClick={onCancel}
                label="Anuluj"
                className="button--stroke button--rounded"
                disabled={isLoading}
              />
              <Button
                type="submit"
                onClick={() => console.log()}
                label={submitLabel}
                className="button--rounded button--black"
                isLoading={isLoading}
              />
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default InvestmentForm;
